import { useState, useMemo } from 'react';
import { useDebounce } from './useDebounce';

type ValidationFunctions<T> = {
  [K in keyof T]?: (value: unknown) => boolean;
};

export function useFilterQueryParams<T extends Record<string, unknown>>(
  initial: T,
  validationFunctions?: ValidationFunctions<T>
) {
  const [filters, setFilters] = useState<T>(initial);

  const debouncedFilters = useDebounce(filters, 0.4);

  const paramsString = useMemo(() => {
    const params = new URLSearchParams();
    Object.entries(debouncedFilters).forEach(([key, value]) => {
      // Check if there's a validation function for this key
      const validationFn = validationFunctions?.[key as keyof T];

      // If validation function exists, use it to determine if value should be included
      if (validationFn) {
        if (validationFn(value as T[keyof T])) {
          if (Array.isArray(value) && value.length > 0) {
            params.set(key, value.join(','));
          } else if (value) {
            params.set(key, value as string);
          }
        }
      } else if (Array.isArray(value) && value.length > 0) {
        // Default behavior when no validation function is provided
        params.set(key, value.join(','));
      } else if (value) {
        params.set(key, value as string);
      }
    });
    return params.toString();
  }, [debouncedFilters, validationFunctions]);

  function setFilter<K extends keyof T>(key: K, value: T[K]) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  return {
    filters,
    setFilter,
    setFilters,
    debouncedFilters,
    paramsString,
  };
}
